/**
 * Bandeau « campagne d'appel » d'une fiche entreprise.
 *
 * Quand la fiche est ouverte depuis une campagne (`?campagne_appel=<id>`), ou
 * simplement quand elle est dans la file d'une campagne active où l'on est
 * affecté, on lit ici — avant le reste de la fiche — la consigne du manager,
 * la solution à proposer et où en est la ligne (tentative 2 sur 3).
 *
 * Le résultat de l'appel se saisit comme d'habitude (« Journaliser ») : c'est
 * le journal qui fait avancer la campagne, pas ce bandeau.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PhoneOutgoing, ArrowLeft } from "lucide-react";
import api from "@/lib/crmApi";

export default function CallCampaignBanner({ companyId, activeId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get(`/call-campaigns/for-company/${companyId}`)
      .then(({ data }) => { if (alive) setRows(Array.isArray(data) ? data : []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [companyId]);
  if (rows.length === 0) return null;
  // La campagne d'où l'on vient d'abord, les autres ensuite.
  const ordered = [...rows].sort((a, b) => (a.id === activeId ? -1 : b.id === activeId ? 1 : 0));
  return (
    <div className="mb-4 space-y-2" data-testid="call-campaign-banner">
      {ordered.map((c) => (
        <div key={c.id}
          className={`flex items-start gap-3 rounded-xl border p-4 ${c.id === activeId
            ? "border-indigo-300 bg-indigo-50" : "border-slate-200 bg-slate-50"}`}>
          <PhoneOutgoing size={20} className={`mt-0.5 shrink-0 ${c.id === activeId ? "text-indigo-600" : "text-slate-400"}`} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-heading font-bold text-slate-900">Campagne d'appel · {c.name}</span>
              <span className="text-xs text-slate-500">
                {c.attempts > 0 ? `Tentative ${c.attempts + 1} sur ${c.retry?.max_attempts ?? "?"}` : "Première tentative"}
              </span>
              {c.solution_nom && (
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-indigo-200">
                  Proposer : {c.solution_nom}
                </span>
              )}
            </div>
            {c.consigne && <p className="mt-1.5 whitespace-pre-line text-sm text-slate-700">{c.consigne}</p>}
            <p className="mt-1.5 text-xs text-slate-500">
              Journalisez l'appel comme d'habitude : la campagne avance toute seule.
            </p>
          </div>
          <Link to={`/campagnes/appel/${c.id}`} className="flex shrink-0 items-center gap-1 text-xs font-medium text-indigo-600 hover:underline">
            <ArrowLeft size={13} /> Retour à la campagne
          </Link>
        </div>
      ))}
    </div>
  );
}
